import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { centsToInput, toCents } from "@/lib/money";
import { BrandMark } from "@/components/BrandMark";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { TagChips } from "../email/RuleEditor";
import { INTERVALS, PRICE_TYPES, PRODUCT_KINDS, kindLabel, mergeFormSettings, priceTypeLabel, type AccessKey, type Price, type Product, type ProductSettings } from "./catalog";
import { productStatus } from "./ProductsPage";
import { AfterPurchaseCard } from "./AfterPurchaseCard";
import { WhatUsesThis } from "../automations/WhatUsesThis";

/** One product: details, checkout settings, prices, and switching it on or off. */
export function ProductDetailPage() {
  const { productId } = useParams({ from: "/billing/products/$productId" });
  const id = Number(productId);
  const q = useQuery({ queryKey: ["product", id], queryFn: () => api<Product>(`/api/v1/products/${id}`) });
  const [onOff, setOnOff] = useState<"on" | "off" | "archive" | "unarchive" | null>(null);
  const [usesOpen, setUsesOpen] = useState(false);
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error?.message ?? "Product not found."}</p>;
  const p = q.data;
  const st = productStatus(p);
  return (
    <div className="mx-auto max-w-[1100px] space-y-3">
      <Link to="/billing/products" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Products
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{p.name}</h1>
        <BrandMark id={p.brand_id} />
        <span className="text-sm text-[var(--color-muted-foreground)]">{kindLabel(p.kind)}</span>
        <Badge variant={st.variant}>{st.label}</Badge>
        <span className="ml-auto flex gap-2">
          {!p.archived_at && (p.active ? (
            <Button size="sm" variant="outline" onClick={() => setOnOff("off")}>
              Switch off
            </Button>
          ) : (
            <Button size="sm" onClick={() => setOnOff("on")}>
              Switch on
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setUsesOpen(true)}>
            What uses this?
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOnOff(p.archived_at ? "unarchive" : "archive")}>
            {p.archived_at ? "Unarchive" : "Archive"}
          </Button>
        </span>
      </div>
      {usesOpen && <WhatUsesThis kind="product" idOrKey={p.id} onClose={() => setUsesOpen(false)} />}
      {p.checkout_url && p.active && !p.archived_at && (
        <p className="text-sm">
          Checkout page:{" "}
          <a href={p.checkout_url} target="_blank" rel="noreferrer noopener" className="text-[var(--color-primary)] hover:underline">
            {p.checkout_url}
          </a>
        </p>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        <DetailsCard p={p} />
        <SettingsCard p={p} />
      </div>
      <AfterPurchaseCard productId={p.id} />
      <PricesCard p={p} />
      {onOff && <OnOffDialog p={p} what={onOff} onClose={() => setOnOff(null)} />}
    </div>
  );
}

function usePatch(id: number) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => api<Product>(`/api/v1/products/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["product", id] });
      void qc.invalidateQueries({ queryKey: ["products"] });
      toast("Saved");
    },
    onError: (e) => toast(e.message, "error"),
  });
}

function OnOffDialog({ p, what, onClose }: { p: Product; what: "on" | "off" | "archive" | "unarchive"; onClose: () => void }) {
  const patch = usePatch(p.id);
  const text = {
    on: {
      title: `Switch ${p.name} on?`,
      body: p.slug
        ? `Customers can then buy it on its checkout page (/${p.slug}) and staff can sell it. Check the prices and settings first; Brandon OKs anything customers can buy.`
        : "Staff can then sell it from the contact screen. It has no checkout page yet, so customers can't buy it online.",
      button: "Switch on",
      body2: { active: true },
    },
    off: { title: `Switch ${p.name} off?`, body: "Nobody can buy it until it's switched on again. Past orders, subscriptions and payment plans carry on.", button: "Switch off", body2: { active: false } },
    archive: { title: `Archive ${p.name}?`, body: "It's hidden everywhere and can't be sold. Past orders keep it.", button: "Archive", body2: { archived: true } },
    unarchive: { title: `Unarchive ${p.name}?`, body: "It shows in the lists again (still switched off until you switch it on).", button: "Unarchive", body2: { archived: false } },
  }[what];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(480px,calc(100vw-2rem))]">
        <DialogTitle>{text.title}</DialogTitle>
        <DialogDescription>{text.body}</DialogDescription>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={what === "on" || what === "unarchive" ? "default" : "destructive"} disabled={patch.isPending} onClick={() => patch.mutate(text.body2, { onSuccess: onClose })}>
            {text.button}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailsCard({ p }: { p: Product }) {
  const patch = usePatch(p.id);
  const keys = useQuery({ queryKey: ["access-keys"], queryFn: () => api<{ keys: AccessKey[] }>("/api/v1/access-keys"), staleTime: 300_000 });
  const [name, setName] = useState(p.name);
  const [kind, setKind] = useState(p.kind);
  const [slug, setSlug] = useState(p.slug ?? "");
  const [sku, setSku] = useState(p.sku ?? "");
  const [description, setDescription] = useState(p.description ?? "");
  const [shipping, setShipping] = useState(p.requires_shipping);
  const [access, setAccess] = useState<string[]>(p.access_keys ?? []);
  const slugBad = !!slug && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="space-y-1">
          <Label htmlFor="pd-name">Name</Label>
          <Input id="pd-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="pd-kind">Kind</Label>
            <Select id="pd-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              {PRODUCT_KINDS.map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pd-sku">SKU (optional)</Label>
            <Input id="pd-sku" value={sku} onChange={(e) => setSku(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pd-slug">Checkout address</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs whitespace-nowrap text-[var(--color-muted-foreground)]">account site /checkout/</span>
            <Input id="pd-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="boot-camp-dallas" />
          </div>
          {slugBad ? (
            <p className="text-xs text-[var(--color-danger-fg)]">Lowercase words joined by dashes, like boot-camp-dallas.</p>
          ) : (
            <p className="text-xs text-[var(--color-muted-foreground)]">Leave it empty for products only staff sell.</p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="pd-desc">Description (shown at checkout)</Label>
          <Textarea id="pd-desc" className="min-h-16" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <label className="flex items-center gap-2">
          <Checkbox checked={shipping} onChange={(e) => setShipping(e.target.checked)} /> Ask for a shipping address (print books)
        </label>
        <div className="space-y-1">
          <div className="text-xs font-medium">Access it gives</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(keys.data?.keys ?? []).map((k) => (
              <label key={k.key} className="flex items-center gap-1.5" title={k.description ?? undefined}>
                <Checkbox checked={access.includes(k.key)} onChange={(e) => setAccess(e.target.checked ? [...access, k.key] : access.filter((x) => x !== k.key))} />
                {k.name}
              </label>
            ))}
            {access
              .filter((a) => !(keys.data?.keys ?? []).some((k) => k.key === a))
              .map((a) => (
                <Badge key={a} variant="muted">
                  {a}
                </Badge>
              ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={patch.isPending || !name.trim() || slugBad}
            onClick={() => patch.mutate({ name: name.trim(), kind, slug, sku, description, requires_shipping: shipping, access_keys: access })}
          >
            Save details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsCard({ p }: { p: Product }) {
  const patch = usePatch(p.id);
  const toast = useToast();
  const s0 = p.settings_parsed ?? {};
  const [s, setS] = useState<ProductSettings>({ ...s0 });
  const set = (x: Partial<ProductSettings>) => setS((v) => ({ ...v, ...x }));
  const [alerts, setAlerts] = useState((s0.alert_emails ?? []).join(", "));
  const [reading, setReading] = useState(false);
  const save = async () => {
    // Settings are replaced as a whole: start from the server's latest copy so after-purchase content (set
    // through Claude) and other settings this form doesn't show are kept.
    setReading(true);
    try {
      const latest = await api<Product>(`/api/v1/products/${p.id}`);
      patch.mutate({ settings: mergeFormSettings(latest.settings_parsed ?? {}, { ...s, alert_emails: alerts.split(/[\s,]+/).filter(Boolean) }) });
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setReading(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Checkout and after purchase</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="space-y-1">
          <Label htmlFor="ps-head">Checkout page title</Label>
          <Input id="ps-head" value={s.headline ?? ""} onChange={(e) => set({ headline: e.target.value })} placeholder={p.name} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ps-thanks">Thank-you page after paying</Label>
          <Input id="ps-thanks" value={s.thank_you_url ?? ""} onChange={(e) => set({ thank_you_url: e.target.value })} placeholder="https://www.mobilehomeuniversity.com/thank-you/" />
          <p className="text-xs text-[var(--color-muted-foreground)]">A thank-you page kept in the CRM (set by Claude) comes first; then this page; then the receipt.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ps-param">productName for the thank-you page</Label>
            <Input id="ps-param" value={s.product_param ?? ""} onChange={(e) => set({ product_param: e.target.value })} placeholder="BootCamp" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ps-terms">Terms page (they must agree)</Label>
            <Input id="ps-terms" value={s.terms_url ?? ""} onChange={(e) => set({ terms_url: e.target.value })} placeholder="optional" />
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium">Tags when the order is paid</div>
          <TagChips ids={s.purchase_tag_ids ?? []} onChange={(v) => set({ purchase_tag_ids: v })} />
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium">Tags while they have access (removed when it ends)</div>
          <TagChips ids={s.access_tag_ids ?? []} onChange={(v) => set({ access_tag_ids: v })} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ps-days">Access lasts (days, one payment; 0 = no end)</Label>
            <Input id="ps-days" type="number" min={0} value={s.access_days ?? 0} onChange={(e) => set({ access_days: Number(e.target.value) || 0 })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ps-alerts">Also tell (staff emails)</Label>
            <Input id="ps-alerts" value={alerts} onChange={(e) => setAlerts(e.target.value)} placeholder="optional" />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <label className="flex items-center gap-1.5">
            <Checkbox checked={!!s.quantity} onChange={(e) => set({ quantity: e.target.checked })} /> Let buyers pick a quantity
          </label>
          <label className="flex items-center gap-1.5">
            <Checkbox checked={!!s.no_affiliate} onChange={(e) => set({ no_affiliate: e.target.checked })} /> No affiliate commission
          </label>
        </div>
        <div className="flex justify-end">
          <Button size="sm" disabled={patch.isPending || reading} onClick={() => void save()}>
            Save checkout settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PricesCard({ p }: { p: Product }) {
  const [editing, setEditing] = useState<Price | "new" | null>(null);
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>Prices</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setEditing("new")}>
          <Plus /> Add a price
        </Button>
      </CardHeader>
      <Table>
        <THead>
          <tr>
            <TH>Price</TH>
            <TH>How they pay</TH>
            <TH>Default</TH>
            <TH>Status</TH>
            <TH className="w-px" />
          </tr>
        </THead>
        <tbody>
          {p.prices.length === 0 && (
            <TR>
              <TD colSpan={5} className="text-[var(--color-muted-foreground)]">
                No prices yet.
              </TD>
            </TR>
          )}
          {p.prices.map((x) => (
            <TR key={x.id} className={x.active ? "" : "row-dim"}>
              <TD>
                <div className="font-medium">{x.summary}</div>
                {(x.name !== priceTypeLabel(x.type) || x.keap_id) && (
                  <div className="text-xs text-[var(--color-muted-foreground)]">
                    {x.name !== priceTypeLabel(x.type) ? x.name : ""}
                    {x.keap_id ? `${x.name !== priceTypeLabel(x.type) ? ", " : ""}from Keap` : ""}
                  </div>
                )}
              </TD>
              <TD className="text-xs">{priceTypeLabel(x.type)}</TD>
              <TD>{x.is_default ? <Badge variant="default">first choice</Badge> : null}</TD>
              <TD>{x.active ? <Badge variant="success">on</Badge> : <Badge variant="muted">off</Badge>}</TD>
              <TD>
                <Button size="sm" variant="ghost" onClick={() => setEditing(x)}>
                  Edit
                </Button>
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
        Changing a price doesn't change anyone already paying it: existing subscriptions and payment plans keep their amounts. Last changed {formatDateTime(p.updated_at)}.
      </p>
      {editing && <PriceDialog product={p} price={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

/** prices_create / prices_update. Type and interval are fixed once made (a new price is a new option). */
function PriceDialog({ product, price, onClose }: { product: Product; price: Price | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(price?.name ?? "");
  const [type, setType] = useState(price?.type ?? "one_time");
  const [amount, setAmount] = useState(centsToInput(price?.amount_cents));
  const [interval, setInterval] = useState(price?.interval ?? "month");
  const [installments, setInstallments] = useState(String(price?.installments ?? 3));
  const [down, setDown] = useState(centsToInput(price?.down_payment_cents));
  const [isDefault, setIsDefault] = useState(price?.is_default ?? product.prices.length === 0);
  const [active, setActive] = useState(price?.active ?? true);
  const cents = toCents(amount);
  const downCents = down ? toCents(down) : 0;
  const save = useMutation({
    mutationFn: () =>
      price
        ? api(`/api/v1/prices/${price.id}`, {
            method: "PATCH",
            json: {
              name: name.trim() || undefined,
              amount_cents: cents,
              installments: type === "payment_plan" ? Number(installments) : undefined,
              down_payment_cents: type === "payment_plan" ? downCents : undefined,
              is_default: isDefault,
              active,
            },
          })
        : api(`/api/v1/products/${product.id}/prices`, {
            json: {
              price: {
                name: name.trim() || undefined,
                type,
                amount_cents: cents,
                interval: type === "one_time" ? undefined : interval,
                installments: type === "payment_plan" ? Number(installments) : undefined,
                down_payment_cents: type === "payment_plan" && downCents ? downCents : undefined,
                is_default: isDefault || undefined,
                inactive: !active || undefined,
              },
            },
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["product", product.id] });
      void qc.invalidateQueries({ queryKey: ["products"] });
      toast(price ? "Price saved" : "Price added");
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>{price ? `Edit ${price.summary}` : "Add a price"}</DialogTitle>
        <DialogDescription>
          {price ? "People already paying this price keep their amount." : "Another way to buy the same product, for example a payment plan or a yearly option."}
        </DialogDescription>
        <div className="space-y-3 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="pr-type">How they pay</Label>
              <Select id="pr-type" value={type} disabled={!!price} onChange={(e) => setType(e.target.value)}>
                {PRICE_TYPES.map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pr-name">Name buyers see (optional)</Label>
              <Input id="pr-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={type === "recurring" ? "Monthly" : type === "payment_plan" ? "3 payments" : "One payment"} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pr-amount">{type === "one_time" ? "Price ($)" : type === "recurring" ? "Each period ($)" : "Each installment ($)"}</Label>
              <Input id="pr-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            {type !== "one_time" && (
              <div className="space-y-1">
                <Label htmlFor="pr-interval">Every</Label>
                <Select id="pr-interval" value={interval} disabled={!!price} onChange={(e) => setInterval(e.target.value)}>
                  {INTERVALS.map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {type === "payment_plan" && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="pr-inst">Installments</Label>
                  <Input id="pr-inst" type="number" min={2} value={installments} onChange={(e) => setInstallments(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pr-down">Down payment today ($)</Label>
                  <Input id="pr-down" inputMode="decimal" value={down} onChange={(e) => setDown(e.target.value)} placeholder="0" />
                </div>
              </>
            )}
          </div>
          {amount && cents === null && <p className="text-xs text-[var(--color-danger-fg)]">Type an amount like 99 or 29.95.</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <label className="flex items-center gap-1.5">
              <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> First choice at checkout
            </label>
            <label className="flex items-center gap-1.5">
              <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} /> On (can be bought)
            </label>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending || cents === null || cents <= 0 || (down !== "" && downCents === null)} onClick={() => save.mutate()}>
            {price ? "Save price" : "Add price"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
